import DateTimePicker from '@ui-components-3/ui/lib/components/DateTimePicker'
import useDocumentTitle from 'hooks/useDocumentTitle'
import TestIds from 'util/TestIds'
import EntryFormCard from '../EntryFormCard'
import { VerifyCreateAccountSchema, VerifyCreateAccountValues } from 'schemas/VerifyCreateAccountSchema'
import FormControl from '@ui-components-3/ui/lib/components/FormControl'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'
import Input from '@ui-components-3/ui/lib/components/Input'
import { useTranslation } from 'react-i18next'

const SCHEDULING_DATE_FORMAT2 = 'MM-dd-yyyy'

export type VerifyCreateAccountProps = {
  onSubmit: (values: VerifyCreateAccountValues) => void
  onBack: () => void
}

const VerifyCreateAccountScene = (props: VerifyCreateAccountProps) => {
  const { onSubmit, onBack } = props
  const { t } = useTranslation()
  useDocumentTitle(t('login.verifyCreateAccount.pageTitle'))

  const {
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
    control,
  } = useForm<VerifyCreateAccountValues>({
    defaultValues: { dob: '', email: '' },
    // TODO: Replace any typing once yup is upgraded
    resolver: yupResolver(VerifyCreateAccountSchema as any),
  })

  return (
    <EntryFormCard prompt={t('login.verifyCreateAccount.prompt')} data-testid={TestIds.login.view.verifyCreateAccount}>
      <p className="typography-body self-start pb-4 text-start text-neutral-600">{t('common.requiredFields')}</p>
      <form
        onSubmit={handleSubmit((values) => onSubmit(values))}
        className="flex flex-col items-center gap-6"
        data-testid={TestIds.login.form.verifyCreateAccount}
      >
        <div className="flex w-full flex-col gap-4">
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <FormControl
                label={t('login.verifyCreateAccount.emailLabel')}
                helperVariant="error"
                helper={isDirty && error ? <p>{error.message}</p> : null}
              >
                <Input
                  data-testid={TestIds.login.input.verifyCreateAccountEmail}
                  placeholder={t('login.verifyCreateAccount.emailPlaceholder')}
                  autoComplete="email"
                  {...field}
                />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="dob"
            render={({ field, fieldState: { error } }) => (
              <FormControl
                label={t('login.verifyCreateAccount.dobLabel')}
                helperVariant="error"
                helper={isDirty && error ? <p>{error.message}</p> : null}
              >
                <DateTimePicker
                  autoComplete="off"
                  disableFuture
                  className="min-w-[15em]"
                  data-testid={TestIds.login.input.verifyCreateAccountDob}
                  format="MM-dd-yyyy"
                  transformBeforeChange={(v: string, formatFn: (value: string, format: string) => void) =>
                    formatFn(v, SCHEDULING_DATE_FORMAT2)
                  }
                  {...field}
                />
              </FormControl>
            )}
          />
        </div>
        <div className="mb-2 flex w-full items-center justify-between gap-4">
          <button
            className="btn btn-neutral-outlined grow"
            data-testid={TestIds.login.button.cancel}
            onClick={onBack}
            type="button"
          >
            {t('common.back')}
          </button>
          <button
            className="btn btn-primary grow"
            data-testid={TestIds.login.button.continue}
            disabled={!isDirty || !isValid || isSubmitting}
            type="submit"
          >
            {t('common.continue')}
          </button>
        </div>
      </form>
    </EntryFormCard>
  )
}

export default VerifyCreateAccountScene
